import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NgxSpinnerService } from 'ngx-spinner';
import { PortalContent } from '../../models/borrower-portal/portal-content-model-model';
import { ReferAFriendRequest, ReferralType } from '../../models/borrower-portal/refer-a-friend-request.model';
import { ReferralAgent } from '../../models/borrower-portal/referral-agent-model';
import { BasePortalService } from '../../services/base-portal.service';
import { EnumsService } from 'projects/borrower-app/src/app/services/enums.service';
import { NotificationService } from '../../services/notification.service';
import { EnumerationItem } from 'projects/shared/models/enumeration-item.model';
import { AddressSelectedEvent } from 'projects/shared/address-autocomplete-v2/address-autocomplete-v2.component';

@Component({
    selector: 'portal-refer-a-friend',
    templateUrl: './refer-a-friend.component.html',
    styleUrls: ['./refer-a-friend.component.scss'],
    standalone: false
})
export class PortalReferAFriendComponent implements OnInit {
  @ViewChild('referAFriendForm') referAFriendForm: NgForm | undefined;

  req: ReferAFriendRequest = new ReferAFriendRequest();

  agents: ReferralAgent[] = [];
  portalContent: PortalContent;

  isSaving: boolean = false;
  termsOpened: boolean = false;
  sanitizedReferralContent: SafeHtml;
  sanitizedReferralTermsContent: SafeHtml;

  referralTypes: ReferralType[] = [ReferralType.Borrower, ReferralType.Agent];
  ReferralType = ReferralType;
  isAgentPortal = false;
  states: EnumerationItem[] = [];
  subjectPropertyAddress?: string;

  // LLS-280 — same list My Info uses, so the referral and the wizard offer identical languages.
  // A getter (not a field) because EnumsService re-resolves the labels against the active language.
  get languagePreferenceOptions(): EnumerationItem[] {
    return this._enumsService.languages;
  }

  constructor(
    private readonly _portalService: BasePortalService,
    private readonly _spinnerService: NgxSpinnerService,
    private readonly _notifyService: NotificationService,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _sanitizer: DomSanitizer,
    private readonly _route: ActivatedRoute,
    private readonly _enumsService: EnumsService
  ) {}

  ngOnInit() {
    this.req.referralType = ReferralType.Borrower;
    this.isAgentPortal = !!this._route.snapshot.data['isAgent'];
    this.states = this._enumsService.stateNames;
    this.loadPortalContent();
    this.loadAgents();
  }

  onReferralTypeChange = () => {
    if (this.req.referralType !== ReferralType.Borrower) {
      this.clearBorrowerReferralFields();
    }
  };

  onSubjectPropertyAddressSelected = (e: AddressSelectedEvent) => {
    this.req.subjectPropertyCity = e.city;
    this.req.subjectPropertyState = e.state?.toUpperCase();
    this.req.subjectPropertyZip = e.zipCode;
  };

  onLanguagePreferenceChange = () => {
    if (this.req.languagePreference !== 'Other') {
      this.req.languagePreferenceOtherDescription = undefined;
    }
  };

  private clearBorrowerReferralFields = () => {
    this.req.loanPurpose = undefined;
    this.req.loanRequestPurpose = undefined;
    this.req.subjectPropertyCity = undefined;
    this.req.subjectPropertyState = undefined;
    this.req.subjectPropertyZip = undefined;
    this.subjectPropertyAddress = undefined;
    this.req.languagePreference = undefined;
    this.req.languagePreferenceOtherDescription = undefined;
  };

  loadAgents = () => {
    this._spinnerService.show();
    this._portalService
      .getReferralAgents()
      .subscribe((agents) => {
        this.agents = agents;
        if (agents.length > 0) {
          this.req.referToUserId = agents[0].userId;
        }
      })
      .add(() => {
        this._spinnerService.hide();
        this._cdr.detectChanges();
      });
  };

  loadPortalContent = () => {
    this._spinnerService.show();
    this._portalService
      .getPortalContent()
      .subscribe((content) => {
        this.portalContent = content;
        if (content.referralContent) {
          this.sanitizedReferralContent = this._sanitizer.bypassSecurityTrustHtml(content.referralContent);
        }
        if (content.referralTermsContent) {
          this.sanitizedReferralTermsContent = this._sanitizer.bypassSecurityTrustHtml(content.referralTermsContent);
        }
      })
      .add(() => {
        this._spinnerService.hide();
        this._cdr.detectChanges();
      });
  };

  submit = () => {
    this.referAFriendForm.form.markAllAsTouched();

    if (this.referAFriendForm.form.valid) {
      this._spinnerService.show();
      this.isSaving = true;
      this._portalService
        .postReferAFriend(this.req)
        .subscribe(() => {
          this._notifyService.showSuccess(
            'Your referral has been submitted successfully.',
            'Success!'
          );

          this.resetFormFields();
        })
        .add(() => {
          this._spinnerService.hide();
          this.isSaving = false;
          this._cdr.detectChanges();
        });
    }
  };

  private resetFormFields = () => {
    this.req.email = '';
    this.req.firstName = '';
    this.req.lastName = '';
    this.req.phone = '';
    this.req.note = '';
    this.clearBorrowerReferralFields();
    setTimeout(() => {
      this.referAFriendForm.form.markAllAsTouched();
      this.referAFriendForm.form.markAsPristine();
      this.referAFriendForm.form.controls['emailAddress'].setErrors(null);
      this.referAFriendForm.form.controls['firstName'].setErrors(null);
      this.referAFriendForm.form.controls['lastName'].setErrors(null);
      this.referAFriendForm.form.controls['phoneNumber'].setErrors(null);
      this.referAFriendForm.form.controls['note'].setErrors(null);
    });
  };
}
